import { CircleAlert, CircleX } from 'lucide-react'
import { Fragment, type ReactNode, useId } from 'react'

import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'

export function TournamentGameState({
  state,
  canContinue = false,
  children,
  className,
}: {
  state: 'retry' | 'void'
  canContinue?: boolean
  children?: ReactNode
  className?: string
}) {
  const { t } = useI18n()
  const titleId = useId()
  const isVoid = state === 'void'
  const Icon = isVoid ? CircleX : CircleAlert
  const description = isVoid
    ? canContinue
      ? t('tournamentVoid.description')
      : null
    : t('tournamentRecovery.description')

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'min-w-0 rounded-xl border border-border bg-card p-4 text-foreground sm:p-5',
        className,
      )}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <Icon
          aria-hidden="true"
          className="mt-0.5 size-8 shrink-0 text-primary sm:size-10"
        />
        <div className="min-w-0 flex-1 space-y-2">
          <p
            id={titleId}
            role="status"
            aria-live="polite"
            aria-atomic="true"
            data-testid={isVoid ? 'tournament-void' : 'tournament-retry-later'}
            className="break-words text-xl font-semibold leading-snug sm:text-2xl"
          >
            {t(isVoid ? 'tournamentVoid.title' : 'tournamentRecovery.tryLater')}
          </p>
          <p
            data-testid={isVoid
              ? 'tournament-void-returned'
              : 'tournament-recovery-retained'}
            className="text-base font-semibold leading-6 text-primary"
          >
            {t(isVoid ? 'tournamentVoid.returned' : 'tournamentRecovery.retained')}
          </p>
          {isVoid && (
            <p
              data-testid="tournament-void-no-result"
              className="text-base leading-6"
            >
              {t('tournamentVoid.noResult')}
            </p>
          )}
        </div>
      </div>
      {description && (
        <p className="mt-4 break-words text-base leading-7 text-muted-foreground">
          {description.split('<br>').map((line, index) => (
            <Fragment key={line}>
              {index > 0 && <br />}
              {line}
            </Fragment>
          ))}
        </p>
      )}
      {children && <div className="mt-4 space-y-3">{children}</div>}
    </section>
  )
}
